import { interpolate } from '@format/interpolate';
import { truncatePrice } from '../../../products';
import { calculateDiscountedPrice } from '../../../offer/helpers';
import { ApiResult } from '../../../api';
import { ACTIONS } from '../constants';
import {
  userContinuesWithExtraSelection,
  userSkipsExtraSelection,
} from '../events';

export const findProductBySku = (products, sku) =>
  products.find(product => product.sku === sku);

export const mapAddonSku = (sku, skuMap = {}) => skuMap[sku] || sku;

export const partitionAddonsByAvailability = (
  selectedAddons = [],
  products,
  skuMap = {},
) => {
  const available = [];
  const unavailable = [];

  selectedAddons.forEach(addon => {
    const mappedSku = mapAddonSku(addon.sku, skuMap);
    const product = findProductBySku(products, mappedSku);

    if (product) {
      available.push(addon);
    } else {
      unavailable.push(addon);
    }
  });

  return { available, unavailable };
};

const resolveAddonProducts = (
  productType,
  treatsAndDentalChews,
  supplementProducts,
) =>
  productType === 'TR' || productType === 'DC'
    ? treatsAndDentalChews
    : supplementProducts;

export const mergeAddonSections = (
  addonSections,
  {
    supplementProducts,
    treatsAndDentalChews,
    skuMap,
    offerType,
    offerAmount,
    formattedAmount,
    petName,
  },
) =>
  addonSections.map(section => {
    const addons = section.addons
      .map(addon => {
        const products = resolveAddonProducts(
          addon.productType,
          treatsAndDentalChews,
          supplementProducts,
        );
        const mappedSku = mapAddonSku(addon.sku, skuMap);
        const product = findProductBySku(products, mappedSku);

        if (!product) {
          return null;
        }

        const discountedPrice = calculateDiscountedPrice(
          product.price,
          offerType,
          offerAmount,
        );

        return interpolate(addon, {
          addonPrice: truncatePrice(product.price),
          addonDiscount: truncatePrice(discountedPrice),
          petName,
          addonSavePercentage: formattedAmount,
        });
      })
      .filter(Boolean);

    return {
      ...section,
      addons,
    };
  });

export const mergeAddonModals = (
  modals,
  {
    supplementProducts,
    treatsAndDentalChews,
    skuMap,
    offerType,
    offerAmount,
    formattedAmount,
  },
) =>
  modals
    .map(modal => {
      const products = [...supplementProducts, ...treatsAndDentalChews];
      const product = findProductBySku(
        products,
        mapAddonSku(modal.sku, skuMap),
      );

      if (!product) {
        return null;
      }

      const discountedPrice = calculateDiscountedPrice(
        product.price,
        offerType,
        offerAmount,
      );

      return interpolate(modal, {
        addonPrice: truncatePrice(product.price),
        addonDiscount: truncatePrice(discountedPrice),
        addonSavePercentage: formattedAmount,
      });
    })
    .filter(Boolean);

export const mapAddonsForAttach = (selectedAddons, skuMap = {}) =>
  selectedAddons.map(addon => ({
    sku: mapAddonSku(addon.sku, skuMap),
    frequency: addon.frequency,
    quantity: addon.quantity,
    recurrence: addon.recurrence,
  }));

export const createExtrasSubmitHandler =
  ({ attachExtras, petId, reporter, send, pet, pets, skuMap, setShowModal }) =>
  async selectedAddons => {
    let result = ApiResult.success();
    const attachResult = await attachExtras(
      petId,
      mapAddonsForAttach(selectedAddons, skuMap),
    );

    result = ApiResult.success(attachResult)
      ? attachResult
      : ApiResult.error.client('Failed to attach extras', attachResult.error);

    ApiResult.match(result, {
      success: () => {
        reporter.tag(
          userContinuesWithExtraSelection(selectedAddons, pet?.breeds),
        );
        send(
          ACTIONS.toCheckout({
            pet,
            pets,
            extras: selectedAddons,
          }),
        );
      },
      error: {
        client: error => {
          reporter.error(error.original);
          setShowModal(true);
        },
        server: () => setShowModal(true),
      },
    });

    return result;
  };

export const createExtrasSkipHandler =
  ({
    submitExtras,
    reporter,
    send,
    pet,
    pets,
    selectedAddons,
    unavailableAddons,
  }) =>
  event => {
    event.preventDefault();

    if (selectedAddons.some(addon => unavailableAddons.includes(addon))) {
      submitExtras(
        selectedAddons.filter(addon => !unavailableAddons.includes(addon)),
      );
    }

    reporter.tag(userSkipsExtraSelection());
    send(ACTIONS.toCheckout({ pet, pets }));
  };
